import { SearchIcon } from '@/common/icons/SearchIcon';
import { InputControl } from '@/components/controls/input-control';
import { Form } from '@/components/ui/form';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { IContourAction } from '@/features/stewardship/models';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MONTHS_LIST } from '../../constants/month';
import { useAgroEvents, useProjectContours, useTable } from '../../hooks';
import useAgroTechnicalEventsStore from '../../store/useAgroTechnicalEventsStore';
import { ActionInspectDialog } from '@/components/dialog';
import { AgrotechContour } from './AgrotechContour';

const CURRENT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

export const AgrotechTable = () => {
  const form = useForm();
  const { t } = useTranslation();
  const { columns } = useTable();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeContour, setActiveContour] = useState('');
  const pageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const currentPage = Number(searchParams.get('page')) || CURRENT_PAGE;
  const activeTab = searchParams.get('month') || MONTHS_LIST[0].value;
  const searchValue = form.watch('search');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data: contours = [] } = useProjectContours();
  const activeCropId = useMemo(
    () => contours.find((c) => c.contour_id === activeContour)?.crop_id,
    [contours, activeContour],
  );
  const { data: agroEvents = [], isLoading: eventsLoading } = useAgroEvents(
    activeContour!,
    activeCropId,
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IContourAction | null>(
    null,
  );
  const {
    actionInspectData,
    actionInspectLoading,
    fetchActionInspect,
    clearActionInspectData,
  } = useAgroTechnicalEventsStore();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue?.toString() ?? '');
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const filteredContours = useMemo(() => {
    if (!debouncedSearch) return contours;

    return contours?.filter((item) =>
      item?.contour_number?.toString().includes(debouncedSearch),
    );
  }, [contours, debouncedSearch]);

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      prev.set('month', value);
      return prev;
    });
  };

  const handleOpen = (action: IContourAction) => {
    setSelectedAction(action);
    setOpenModal(true);
  };

  useEffect(() => {
    if (openModal && selectedAction) {
      fetchActionInspect({
        farmer_action_by_project_id:
          selectedAction?.farmer_action_by_project_id,
        farmer_action_by_project_contour_id:
          selectedAction?.farmer_action_by_project_contour_id,
        contour_id: activeContour,
      });
    } else if (!openModal) {
      setSelectedAction(null);
    }
  }, [openModal, selectedAction]);

  return (
    <Form {...form}>
      <div className="flex w-full flex-1 flex-col gap-5 overflow-y-auto">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold">{t('all.contours')}</h4>
          <div className="relative">
            <InputControl
              isNumber
              name="search"
              control={form.control}
              inputClass="outline-none shadow-none ring-0 ring-offset-0 focus:ring-0 focus-visible:ring-0 focus:outline-none focus-visible:outline-none"
              placeholder={t('contour.number')}
            />
            <SearchIcon
              stroke="green"
              className="absolute right-2.5 top-2.5 h-5 w-5 text-green"
            />
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className={'flex-1 overflow-y-auto'}
        >
          <ScrollArea className="w-full">
            <TabsList className="flex w-max flex-nowrap gap-2 px-1">
              {MONTHS_LIST.map((tab) => (
                <TabsTrigger
                  key={tab?.code}
                  value={tab.value}
                  className="min-w-[100px] shrink-0 whitespace-nowrap"
                >
                  {t(tab.label)}
                </TabsTrigger>
              ))}
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          <TabsContent
            value={activeTab}
            className="mt-4 w-full flex-1 rounded-lg"
          >
            <ScrollArea className="w-full">
              <AgrotechContour
                contours={filteredContours}
                activeContour={activeContour}
                onContourChange={setActiveContour}
                agroEvents={agroEvents}
                eventsLoading={eventsLoading}
                columns={columns}
                pageSize={pageSize}
                currentPage={currentPage}
                onRowClick={handleOpen}
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </div>
      <ActionInspectDialog
        open={openModal}
        onOpenChange={setOpenModal}
        selectedAction={selectedAction}
        actionInspectData={actionInspectData}
        actionInspectLoading={actionInspectLoading}
        onClear={clearActionInspectData}
      />
    </Form>
  );
};

// https://gov-api.digitagro.uz/api/farmer-actions-by-project/inspect/03536e13-a57a-49f6-b687-e425f6a169af?farmer_action_by_project_contour_id=6cb0bafe-87d3-4441-915b-10da33a3e15f
